import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CustomModal from "@components/CustomModal";
import ImageImportTool from "@components/ImageImportTool/index.js";

import { Input, Text } from "@fragments";

import { InsertPhotoRounded, SettingsRounded } from "@mui/icons-material";

import api from "@helpers/api";

import { AssetCategory } from "@features/uploadAsset/constants/assetCategories";
import { requestPresignedUpload } from "@features/uploadAsset/api/requestPresignedUpload";
import { putToPresignedUrl } from "@features/uploadAsset/utils/putToPresignedUrl";
import { mapFileToFileInfo } from "@features/uploadAsset/utils/mapFileToFileInfo";

import "./styles.scss";

async function dataUrlToFile(dataUrl, filename = "banner.jpg") {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

async function uploadBannerForCommunity(communityId, dataUrl) {
    const file = await dataUrlToFile(dataUrl, "community-banner.jpg");
    const file_info = mapFileToFileInfo(file);
    const { upload_url, final_url } = await requestPresignedUpload(communityId, {
        category: AssetCategory.COMMUNITY_BANNER,
        file_info,
    });
    await putToPresignedUrl(upload_url, file, file_info.content_type);
    return final_url;
}

export default function CreateCommunityModal({
    open,
    onClose,
    onSuccess,
    // After a successful create, navigate to the new community + reload (default).
    // Callers that want to stay on the current page (e.g. the mentoring attach
    // flow, §6.10) pass redirectOnSuccess={false} and handle the result in
    // onSuccess instead.
    redirectOnSuccess = true,
}) {
    const { t } = useTranslation("contentModals");
    const [loading, setLoading] = useState(false);

    const REF_data = useRef({
        name: "",
        description: "",
        bannerDataUrl: null,
    });

    const [bannerPreview, setBannerPreview] = useState(null);

    function handleChange(key, value) {
        REF_data.current[key] = value;
    }

    async function handleSubmit() {
        const data = REF_data.current;

        // Validate name
        if (!data.name || data.name.trim().length === 0) {
            window.setSnackbar(t("createCommunity.errors.nameRequired"));
            return;
        }
        const nameLength = data.name.trim().length;
        if (nameLength < 3 || nameLength > 25) {
            window.setSnackbar(t("createCommunity.errors.nameLength"));
            return;
        }

        const payload = {
            name: data.name.trim(),
        };

        if (data.description && data.description.trim().length > 0) {
            payload.description = data.description.trim();
        }

        try {
            setLoading(true);

            // Step 1: create the community (without banner — backend wants a URL,
            // and we need the community id before we can presign an upload).
            const { data: response } = await api.post("/community", payload);

            if (!response?.id) {
                window.setSnackbar(t("createCommunity.errors.createFailed"));
                return;
            }

            // Step 2: if the user picked a banner, upload it through the
            // presign flow and patch the community with the resulting URL.
            if (data.bannerDataUrl) {
                try {
                    const bannerUrl = await uploadBannerForCommunity(
                        response.id,
                        data.bannerDataUrl,
                    );
                    await api.patch(`/community/${response.id}`, { banner: bannerUrl });
                    response.banner = bannerUrl;
                } catch (uploadError) {
                    console.error(uploadError);
                    window.setSnackbar(
                        t("createCommunity.errors.bannerFailed"),
                    );
                }
            }

            window.setSnackbar(t("createCommunity.success"));

            onSuccess?.(response);
            handleClose();

            if (redirectOnSuccess && response?.slug) {
                window.navigate?.("/" + response.slug);
                window.location.reload();
            }
        } catch (e) {
            console.error(e);
            window.setSnackbar(t("createCommunity.errors.createFailed"));
        } finally {
            setLoading(false);
        }
    }

    function handleClose() {
        REF_data.current = { name: "", description: "", bannerDataUrl: null };
        setBannerPreview(null);
        onClose?.();
    }

    const bodyComponent = (
        <div className="create-community-modal-body px-5 pb-3 pt-4">
            <Text.h3
                value={t("createCommunity.generalSettings")}
                left={<SettingsRounded />}
                className="mb-2"
            />
            <Input.Text
                variant="dark"
                className="mt-2"
                label={t("createCommunity.nameLabel")}
                size={25}
                placeholder={t("createCommunity.namePlaceholder")}
                onInput={(v) => handleChange("name", v)}
            />

            <Input.Text
                variant="dark"
                className="mt-3"
                label={t("createCommunity.descriptionLabel")}
                size={250}
                multiline
                emoji
                placeholder={t("createCommunity.descriptionPlaceholder")}
                onInput={(v) => handleChange("description", v)}
            />

            <div className="mt-4">
                <Text.h3
                    value={t("createCommunity.avatarTitle")}
                    left={<InsertPhotoRounded />}
                    className="mb-2"
                />
                <Text.h5
                    contrast={5}
                    value={t("createCommunity.avatarHint")}
                />
                <ImageImportTool
                    className="mt-2 banner-upload-creation"
                    variant="dark"
                    src={bannerPreview}
                    size={135}
                    onCrop={(imageBase64) => {
                        handleChange("bannerDataUrl", imageBase64);
                        setBannerPreview(imageBase64);
                    }}
                    title={t("createCommunity.avatarCropTitle")}
                />
            </div>
        </div>
    );

    return (
        <CustomModal
            open={open}
            handleClose={handleClose}
            title={t("createCommunity.modalTitle")}
            maxWidth={520}
            loading={loading}
            bodyComponent={bodyComponent}
            bottomButtonSumbitText={t("createCommunity.submitButton")}
            handleSubmit={handleSubmit}
            disabled={loading}
        />
    );
}
